import React from 'react';
import { ArrowRight, Search, Plug } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import IntegrationsCarousel from '../components/IntegrationsCarousel';
import { FinalCTA } from '../components/Sections';
import { FEATURED_INTEGRATIONS } from '../mock';
import { Button } from '../components/ui/button';

const allByCategory = {
  'Canais de mensageria': [
    'WhatsApp',
    'Instagram',
    'Facebook',
    'Telegram',
    'TikTok',
    'X (Twitter)',
    'SMS',
    'Voz (Voice)',
  ],
  'E-mail, Web e API': [
    'E-mail (IMAP/SMTP)',
    'Chat do site (Widget)',
    'API personalizada (API Channel)',
  ],
  'IA, Linguagem e Tempo Real': [
    'Dialogflow',
    'Google Translate',
    'Cloudflare RealtimeKit',
  ],
  'Produtividade & CRM': [
    'Slack',
    'Linear',
    'LeadSquared',
  ],
};

const Card = ({ name }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white border border-slate-100 p-3 hover:border-[#FF5B24]/40 hover:shadow-md transition-all cursor-pointer group">
    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#FF5B24] flex items-center justify-center font-black text-slate-500 group-hover:text-white transition-colors">
      {name.charAt(0)}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-[#0a0e27] truncate">{name}</div>
      <div className="text-[10px] text-slate-400">Integração nativa</div>
    </div>
    <ArrowRight size={14} className="text-slate-300 group-hover:text-[#FF5B24] transition-colors" />
  </div>
);

const Integracoes = () => {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Integrações e API"
        description="Conecte WhatsApp, Instagram, e-mail e suas ferramentas favoritas à CoreDesk. Integrações nativas + API e webhooks para automatizar toda a operação."
        path="/integracoes"
      />
      <Header />
      <PageHero
        eyebrow="Integrações"
        title="Conecte tudo. |Automatize o resto."
        subtitle="A CoreDesk se integra nativamente com múltiplos canais, ferramentas de produtividade e IA. E, com API personalizada + webhooks, se conecta com qualquer sistema."
      />
      <IntegrationsCarousel />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a0e27]">Catálogo completo</h2>
              <p className="mt-2 text-slate-600">Explore todas as integrações disponíveis por categoria.</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar integração..."
                className="w-full h-11 pl-10 pr-4 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[#FF5B24]"
              />
            </div>
          </div>

          {Object.entries(allByCategory).map(([cat, items]) => (
            <div key={cat} className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Plug size={14} className="text-[#FF5B24]" />
                <h3 className="text-lg font-extrabold text-[#0a0e27]">{cat}</h3>
                <span className="text-xs text-slate-400">({items.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {items.map((n) => <Card key={n} name={n} />)}
              </div>
            </div>
          ))}

          <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#0a0e27] to-[#1a1f4d] p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#FF5B24]/30 rounded-full blur-3xl" />
            <h3 className="relative text-2xl md:text-3xl font-extrabold text-white">Não achou sua ferramenta?</h3>
            <p className="relative mt-3 text-white/70 max-w-xl mx-auto">
              Nossa API aberta e os canais customizados (API Channel + Webhooks) permitem integrar praticamente qualquer sistema. Fale com a gente e criamos uma solução sob medida.
            </p>
            <div className="relative mt-6">
              <Button onClick={() => navigate('/contato')} className="btn-coral rounded-full h-12 px-6 text-sm font-semibold">
                Solicitar integração custom <ArrowRight size={15} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </>
  );
};

export default Integracoes;
